import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Divider, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InfoText from "../../../shared/components/InfoText";
import { FlexContainer } from "../../../shared/components/styled/Container.styled";
import { ActionText } from "../../../shared/components/styled/Typography.styled";
import { PillButton } from "../../../shared/components/styled/Button.styled";
import TwoToneItem from "../../../shared/components/dashboard/TwoToneItem";
import RowItem from "../../../shared/components/dashboard/RowItem";
import { hex2rgba } from "../../../shared/utils/utility-functions";
import { isLoanClosedToPayments } from "../../../shared/utils/payment-functions";
import { NOT_APPLICABLE } from "../../../shared/constants/Defaults.constants";
import { AuthContext } from "../../../contexts/AuthContext";
import {
  CLIENT_ADMIN_ROLE,
  PAYMENTS_BORROWER_ROLE,
} from "../../../shared/constants/Roles.constants";
import { useMediaQuery } from "@mui/material";

const DashboardPaymentDetails = ({
  dueDate = "",
  autopay = false,
  amountDue = "",
  totalAmountDue = "",
  showTotalAmountDue = false,
  principalInterestAmount = "",
  chargesAmount = NOT_APPLICABLE,
  escrowAmount = "",
  lastPaymentReceived = "",
  autoPaymentDate = "",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const { impersonatedUser, userRoles, activeLoanDetails } =
    useContext(AuthContext);
  const loanClosedToPayments = isLoanClosedToPayments(
    activeLoanDetails?.loanDetails,
  );

  // The AutoPay block keeps its own markup wherever it lands, so the two layouts cannot
  // drift apart as the card changes.
  const autoPayNotice = autopay ? (
    <FlexContainer
      flexdirection="column"
      gap="5px"
      background={hex2rgba(theme.palette.primary.main, 0.1)}
      padding="10px"
      width="100%"
    >
      <InfoText
        Icon={InfoOutlinedIcon}
        iconColor="primary"
        text="AutoPay is activated."
        textColor="text.secondary"
        textSize={12}
        textWeight="bold"
      />
      {!impersonatedUser &&
      userRoles?.includes(PAYMENTS_BORROWER_ROLE) &&
      !loanClosedToPayments ? (
        <ActionText>
          <Typography onClick={(e) => navigate("/payments/activity")}>
            Manage AutoPay
          </Typography>
        </ActionText>
      ) : null}
    </FlexContainer>
  ) : (
    ""
  );

  return (
    <FlexContainer
      flexdirection="column"
      padding="20px"
      gap="15px"
      alignitems="center"
      justifycontent="center"
      height="100%"
    >
      <FlexContainer
        width="100%"
        justifycontent="space-between"
        gap="10px"
        height="100%"
      >
        <FlexContainer flexdirection="column" alignitems="flex-start">
          <Typography fontWeight="bold" fontSize="1.125rem">
            {showTotalAmountDue ? "Total Amount Due" : "Amount Due"}
          </Typography>
          {/* The draft date moves down to its own row below the breakdown when the
              total is shown, so it is not repeated here. */}
          {!showTotalAmountDue ? (
            <Typography fontWeight="bold" color="text.secondary">
              {autoPaymentDate ? `Due ${autoPaymentDate}` : ""}
            </Typography>
          ) : null}
        </FlexContainer>

        <TwoToneItem
          breakpoint="."
          data={showTotalAmountDue ? totalAmountDue : amountDue}
          prefix="$"
          toneOne="primary"
          toneTwo="primary"
          fontSize="2.2857rem"
          prefixFontSize="1.1428rem"
        />
      </FlexContainer>
      {!showTotalAmountDue ? autoPayNotice : null}
      <Divider flexItem />
      {/* The rows below always describe a single month's payment. Saying so keeps them
          from reading as a breakdown of the total above them. */}
      {showTotalAmountDue ? (
        <FlexContainer width="100%" justifycontent="flex-start">
          <Typography fontWeight="bold">
            Minimum Monthly Payment Breakdown
          </Typography>
        </FlexContainer>
      ) : null}
      <RowItem
        label="Principal & Interest"
        value={principalInterestAmount}
        prefix="$"
      />
      {chargesAmount !== NOT_APPLICABLE && (
        <RowItem
          label="Charges"
          value={chargesAmount}
          prefix="$"
        />
      )}
      {escrowAmount !== NOT_APPLICABLE && (
        <RowItem
          label="Escrow"
          value={escrowAmount}
          prefix="$"
        />
      )}
      <RowItem label="Actual Due Date" value={dueDate} />
      <RowItem label="Last Payment Received" value={lastPaymentReceived} />
      {showTotalAmountDue && autopay ? (
        <RowItem label="Auto Pay Draft Date" value={autoPaymentDate} />
      ) : null}
      {showTotalAmountDue ? autoPayNotice : null}
      {!loanClosedToPayments ? (
        <PillButton
          variant="contained"
          color="primary"
          width={isMobile ? "100%" : "50%"}
          customfontsize="1.25rem"
          onClick={(e) => navigate("/payments/new")}
          disabled={
            !!impersonatedUser || userRoles?.includes(CLIENT_ADMIN_ROLE)
          }
        >
          Make a Payment
        </PillButton>
      ) : (
        ""
      )}
    </FlexContainer>
  );
};

export default DashboardPaymentDetails;